import { useState, useRef } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import IconButton from '@mui/material/IconButton';
import 'dayjs/locale/pt-br';
import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const FORMAT = 'DD/MM/YYYY HH:mm';
const DATE_ONLY_FORMAT = 'DD/MM/YYYY';

interface DateTimePickerFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

export default function DateTimePickerField({
  label,
  value,
  onChange,
  required,
  error,
  helperText,
}: DateTimePickerFieldProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState<Dayjs | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const parsedDayjs: Dayjs | null = value ? dayjs(value, FORMAT, true) : null;
  const displayValue: Dayjs | null = parsedDayjs && parsedDayjs.isValid() ? parsedDayjs : pickerValue;

  const handlePickerChange = (newVal: Dayjs | null) => {
    setPickerValue(newVal);
    if (newVal && newVal.isValid()) {
      onChange(newVal.format(FORMAT));
    }
  };

  const handlePickerAccept = (newVal: Dayjs | null) => {
    if (newVal && newVal.isValid()) {
      onChange(newVal.format(FORMAT));
    }
    setPickerOpen(false);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(applyMask(e.target.value));
  };

  const handleTextBlur = () => {
    const normalizedValue = completeDateOnlyWithMidnight(value);

    if (normalizedValue !== value) {
      onChange(normalizedValue);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <TextField
        fullWidth
        size="small"
        label={label}
        value={value}
        onChange={handleTextChange}
        onBlur={handleTextBlur}
        placeholder="dd/mm/yyyy hh:mm"
        required={required}
        error={error}
        helperText={helperText}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                ref={anchorRef}
                size="small"
                edge="end"
                onClick={() => setPickerOpen(true)}
                tabIndex={-1}
                sx={{ color: error ? 'error.main' : 'action.active' }}
              >
                <CalendarMonthOutlinedIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <DateTimePicker
        open={pickerOpen}
        onOpen={() => setPickerOpen(true)}
        onClose={() => setPickerOpen(false)}
        value={displayValue}
        onChange={handlePickerChange}
        onAccept={handlePickerAccept}
        closeOnSelect={false}
        format={FORMAT}
        ampm={false}
        views={['year', 'month', 'day', 'hours', 'minutes']}
        slotProps={{
          textField: { sx: { display: 'none' } },
          popper: {
            anchorEl: anchorRef.current,
            placement: 'bottom-end',
            modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
          },
          actionBar: { actions: ['cancel', 'accept'] },
        }}
      />
    </LocalizationProvider>
  );
}

function applyMask(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 12);
  let result = '';

  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) result += '/';
    else if (i === 8) result += ' ';
    else if (i === 10) result += ':';

    result += digits[i];
  }

  return result;
}

function completeDateOnlyWithMidnight(raw: string): string {
  const trimmed = raw.trim();

  if (!trimmed || trimmed.length !== DATE_ONLY_FORMAT.length) {
    return raw;
  }

  const parsedDate = dayjs(trimmed, DATE_ONLY_FORMAT, true);

  if (!parsedDate.isValid()) {
    return raw;
  }

  return parsedDate.hour(0).minute(0).format(FORMAT);
}
